// Read-only Accounts Summary rollup (planning.md §4). Sits directly below the
// Line Items Summary on every screen that shows line items: one row per distinct GL
// account chosen, with that account's summed line total, sorted descending by
// total, plus a grand-total row (which equals the line items total by
// construction — the two blocks read against each other at a glance).
//
// Cross-company splits break down by company: when a line is pinned to a
// company other than the record's, a leading Company column appears, rows are
// keyed by company + account (accounts descending within, companies ordered by
// subtotal), with a per-company subtotal above the grand total — mirroring the
// line items table's own conditional Company column.
//
// Unassigned lines group under a single "(No account)" row so the totals still
// add up and the gap stays visible. A HALF-CODED line — one that has an expense
// type and is waiting on a cost center — groups there too, but the fact that it
// is half-coded is NOT written into the table as a row (owner, 2026-08-11: "do
// not write 'Awaiting cost center' into a row within the Accounts Summary
// table"). It is stated once beneath the grand total instead, in the same
// `DotPill` shape as the Line Items Summary's "Reconciles" chip.
//
// That line carries the half-coded DOLLARS, not just a count. A count cannot say
// whether the gap is $40 of paper towels or half the invoice, and the amount is
// the part a reviewer acts on — so dropping the labelled row must not drop the
// figure with it. The pill states both.
//
// THE EDITABLE FORM'S OWN MOUNT GROUPS BY EXPENSE TYPE, not by account
// (`groupBy="expense_type"`) — neither step `EditableInvoiceForm` covers
// (Import Control or AP/AR) ever holds a cost center, so a `ChartImplied` line
// there already carries both halves and grouping by account would render
// "689109-67-520 / A&G COPIER" on a screen with no cost center to show.
// UPDATE, item 3 (2026-08-13): this component no longer mounts at Import
// Control at all — that step's screen was cut down to the Company picker,
// Comments and Save — so in practice `groupBy="expense_type"` now only ever
// fires at AP/AR Approval, which still holds no cost center either.
//
// Derived display only — never editable, never written back. One shared
// component so the surfaces can't drift; callers pass a flat row list and this
// groups/sums.
//
// Presented as a peer section of the Line Items Summary it follows — same heading
// component, same divider, same table-header styling. It used to render its own
// micro-caps heading and column labels, which read as a different design
// language from every block around it (user note 2026-08-02).
//
// THE GRAND TOTAL ROW carries a green "Reconciles" `DotPill` INLINE with the
// Total amount (owner, 2026-08-14, reverting item 15/2026-08-13's move of the
// pill to its own right-aligned row below the table — the Line Items Summary's
// own `ReconcileFooter` in `InvoiceLineItemsTable.tsx` never left that pattern,
// and the owner asked for the two to read the same way) when `subtotal`/`tax`/
// `total` are supplied and pass the same `checkAmounts` test as that footer
// (owner item 16, 2026-08-12) — never a red one; a mismatch just shows no pill.
// See `totalCell`'s comment below for how "inline" is done without breaking
// the amount COLUMN's own right-edge alignment with the account rows above it.
//
// The "Distribute tax" toggle (owner, 2026-08-14) sits in that same inline
// group, immediately left of the pill: OFF (default) shows each account's raw
// pre-tax subtotal, exactly as this component always has; ON, it shows the
// SAME figures the Point Click Care export will actually write to the CSV, by
// running the invoice's tax through the identical proportional-spread rule the
// export uses. See `app/src/lib/taxDistribution.ts` for the algorithm and why
// it is a hand-written twin rather than a shared import.
//
// A bolded label sits last in that inline group, right against the amount
// cell (owner, 2026-08-14 — the same treatment, from the same shared
// `useSummaryLabelStyles` class, as the Line Items Summary's own
// `ReconcileFooter`): "Invoice Subtotal:" while the toggle is off (or absent),
// "Invoice Total:" once it is on and actually distributing — naming what the
// grand-total figure beside it actually is, the same way the toggle already
// changes what that figure actually shows.
import {
  Divider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
// PORT NOTE: the `compact` heading variant existed for the claim detail modal,
// which titled its sections differently. Claims is gone, so this renders one
// way — as a peer section of the Line Items Summary above it.
import { useState } from 'react'
import { checkAmounts, formatCurrency, toNumber } from '../lib/format'
import { TINT_PILLS, useSummaryLabelStyles } from '../lib/theme'
import { DotPill } from './Badges'
import { distributeTax, type DistributableRow } from '../lib/taxDistribution'

/** One line's contribution: which account (+ optional company) and how much. */
export interface AccountsSummaryRow {
  accountId?: string | null
  accountName?: string | null
  /** "What is it?" — the grouping key at Import Control. */
  expenseTypeCode?: string | null
  expenseTypeLabel?: string | null
  companyId?: string | null
  companyName?: string | null
  total: number
  /** The line's own sequence (`line_number`) — the tie-break key the "Distribute
      tax" toggle uses to place a rounding remainder exactly where the export
      would (`app/src/lib/taxDistribution.ts`). Optional: a caller that omits it
      still gets a correct SUM, just not a guaranteed-identical tie-break on the
      rare exact-tie case — see that module's `DistributableRow` comment. */
  lineNumber?: number | null
}

interface Props {
  rows: AccountsSummaryRow[]
  /**
   * `'expense_type'` at Import Control, where the cost center must not appear.
   * `'account'` everywhere else. Defaults to `'account'`.
   */
  groupBy?: 'account' | 'expense_type'
  /** The parent record's company id. A leading Company column + per-company
      breakdown appears only when some row's company differs from it (a genuine
      cross-company split). */
  recordCompanyId?: string | null
  /** Invoice amounts, for the green "Reconciles" pill on the Total row — the
      same DotPill on the same `checkAmounts` test as the Line Items Summary
      footer above (`InvoiceLineItemsTable`'s ReconcileFooter, owner item 16,
      2026-08-12). Omitted (or all null) = no pill, never a red one. Also what
      the "Distribute tax" toggle spreads: no amounts known = nothing to
      distribute, so the toggle does not render at all; a known, zero tax
      renders it disabled rather than hidden (see the toggle's own comment). */
  subtotal?: number | string | null
  tax?: number | string | null
  total?: number | string | null
}

const NO_ACCOUNT = '(No account)'
const NO_EXPENSE_TYPE = '(No expense type)'

/** A line with an expense type and no cost center: coded, just not finished.
    Reported beneath the total rather than as a row of its own. */
function isAwaitingCostCenter(r: AccountsSummaryRow): boolean {
  return !r.accountId?.trim() && !r.accountName?.trim() && !!r.expenseTypeCode?.trim()
}

const useStyles = makeStyles({
  // Peer section on the invoice surfaces: rule, heading, table — the same
  // rhythm (and the same 16px gaps) as the Line Items Summary above it.
  wrap: { display: 'flex', flexDirection: 'column', gap: '16px' },
  table: { tableLayout: 'fixed', width: '100%', fontVariantNumeric: 'tabular-nums' },
  num: { textAlign: 'right' },
  numHead: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  subtotalRow: { color: tokens.colorNeutralForeground2 },
  subtotalLabel: { fontStyle: 'italic' },
  grandRow: { fontWeight: tokens.fontWeightSemibold },
  // THE TOTAL ROW'S LABEL CELL — "Total" plus, on the SAME line, the
  // "Distribute tax" toggle and the "Reconciles" pill, right-hugging the
  // amount cell beside it (owner, 2026-08-14: inline with the total, the way
  // `InvoiceLineItemsTable`'s `ReconcileFooter` already reads).
  //
  // HOW THE AMOUNT COLUMN'S ALIGNMENT IS PRESERVED: the amount cell itself
  // (`style={{ width: 120 }}` + `s.num`, tabular-nums) is untouched — this
  // class lives on the LABEL cell next door, not the amount cell, so nothing
  // here changes that cell's width, its `text-align`, or the fixed-layout
  // column it shares with every account row above it. `justifyContent:
  // 'space-between'` puts "Total" on the left and the toggle+pill group hard
  // against this cell's OWN right edge — which is the amount cell's left
  // edge — so the group reads as sitting immediately left of the amount,
  // without the amount cell ever having to make room for a pill inside it
  // (the thing item 15/2026-08-13 broke alignment trying to do; verified by
  // re-running the layout: every account-row amount, the grand total, and
  // this row's amount all still share the same 120px right-aligned column).
  totalCell: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' },
  // The toggle + pill group itself, right-hugged within `totalCell`.
  totalControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  // `wrap` is a flex COLUMN, so its default `align-items: stretch` would draw an
  // inline-flex pill as a full-width band. Right-align it under the Total column
  // the way the Line Items Summary's own chip sits under its total. Only the
  // awaiting-a-cost-center pill uses this now — the Reconciles pill moved
  // inline into `totalCell` above (owner, 2026-08-14, reverting item 15,
  // 2026-08-13) — but it is kept as its own class rather than folded into
  // `wrap` because a second pill line may join it later and both would still
  // want the same rule.
  pillLine: { display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' },
})

function accountLabel(r: AccountsSummaryRow): string {
  const name = r.accountName?.trim()
  if (name) return name
  const id = r.accountId?.trim()
  if (id) return id
  return NO_ACCOUNT
}

function expenseTypeLabel(r: AccountsSummaryRow): string {
  const label = r.expenseTypeLabel?.trim()
  if (label) return label
  return r.expenseTypeCode?.trim() || NO_EXPENSE_TYPE
}

/** One grouped account/expense-type row, as rendered — and, for the account
    grouping, as fed into `distributeTax` (`sequence` is its tie-break key).
    `sequence` is narrowed to required: `byAccount` below always sets it, even
    when every source row omitted `lineNumber` (it then just ties every group
    at `0` — see that function's own comment). */
interface GroupedRow extends DistributableRow {
  key: string
  label: string
  sequence: number
}

/** { key, label, total, sequence } grouped by account, sorted descending by
    total. Keyed by account id, else name — a flow-written row can carry a name
    with no id, and must get its own correctly-labelled row (two id-less named
    accounts stay distinct) rather than joining the "(No account)" bucket. Only
    rows with neither id nor name land there.

    `sequence` is the EARLIEST `lineNumber` rolled into the group — the same
    tie-break key `exportBuilder.ts` uses for its own rounding remainder — with
    rows carrying no `lineNumber` at all tying at `0` (see `AccountsSummaryRow`
    and `taxDistribution.ts`'s `DistributableRow` for why that is a known,
    display-only gap rather than a bug). */
function byAccount(
  rows: AccountsSummaryRow[],
  groupBy: 'account' | 'expense_type',
): GroupedRow[] {
  const map = new Map<string, GroupedRow>()
  for (const r of rows) {
    const key =
      groupBy === 'expense_type'
        ? r.expenseTypeCode?.trim() || NO_EXPENSE_TYPE
        : r.accountId?.trim() || r.accountName?.trim() || NO_ACCOUNT
    const seq = r.lineNumber ?? 0
    const existing = map.get(key)
    if (existing) {
      existing.total += r.total
      existing.sequence = Math.min(existing.sequence, seq)
    } else {
      map.set(key, {
        key,
        label: groupBy === 'expense_type' ? expenseTypeLabel(r) : accountLabel(r),
        total: r.total,
        sequence: seq,
      })
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

export function AccountsSummary({ rows, recordCompanyId, groupBy = 'account', subtotal, tax, total }: Props) {
  const s = useStyles()
  const ls = useSummaryLabelStyles()
  // The toggle's own state. A VIEW preference only — never sent anywhere, never
  // read from anywhere — so it starts OFF (today's behaviour) on every mount
  // and carries no memory between invoices, same as any other display option
  // on this read-only rollup.
  const [distribute, setDistribute] = useState(false)
  if (!rows || rows.length === 0) return null

  const showCompany = rows.some((r) => r.companyId && r.companyId !== (recordCompanyId ?? undefined))
  // RAW pre-tax sum — the reconcile check below is ALWAYS against this figure,
  // never against the tax-distributed one: the toggle changes what is
  // DISPLAYED, never what is CHECKED. Flipping a view preference must not turn
  // a green pill red or vice versa.
  const grand = rows.reduce((sum, r) => sum + r.total, 0)
  // Green only. A red "Doesn't reconcile" here would restate, six inches lower,
  // the red pill the Line Items footer already shows. With no printed amounts
  // there is nothing to reconcile against and nothing renders.
  const amountsKnown = subtotal != null || tax != null || total != null
  const reconciles = amountsKnown && checkAmounts(grand, subtotal, tax, total).reconciles

  // Gated to the account grouping: AP/AR Approval (the only step that still
  // mounts this component with `groupBy="expense_type"` — it no longer mounts
  // at Import Control at all, owner item 3, 2026-08-13) never populates an
  // account either, so ungated this would claim every line on a screen that
  // has no cost center to report.
  const awaiting = groupBy === 'account' ? rows.filter(isAwaitingCostCenter) : []
  const awaitingTotal = awaiting.reduce((sum, r) => sum + r.total, 0)

  // "DISTRIBUTE TAX" — gated to the account grouping for the same reason as
  // `awaiting` above: `groupBy="expense_type"` (AP/AR Approval) never composes
  // a whole GL account, so there is no account pool to spread tax across in
  // the shape the export actually spreads it across. Also gated to KNOWN
  // amounts — with none, there is nothing to distribute against, which is the
  // same condition that hides the Reconciles pill above, so the toggle hides
  // with it rather than rendering a control that can never do anything.
  const canDistributeTax = groupBy === 'account' && amountsKnown
  const taxDollars = toNumber(tax)
  // A KNOWN but ZERO tax disables rather than hides: hiding a control the
  // reviewer has used on other invoices reads as broken; disabled says
  // plainly "there is genuinely nothing to distribute here" (owner asked for
  // one or the other and to say which — this is the one chosen, and why).
  const distributing = canDistributeTax && distribute && taxDollars !== 0

  // The invoice's full pool of account groups — EVERY company it touches, not
  // one company's subset — because that is the pool `exportBuilder.ts` spreads
  // tax across (`docs/export.md` → Sales tax: spreading within one company's
  // subset would spread the whole tax again on a split invoice). Computed
  // once regardless of `showCompany` so the toggle behaves identically either
  // way; `perCompany` below is only how the SAME flat pool is re-presented.
  const perCompany = showCompany ? buildCompanyGroups(rows, groupBy) : null
  const flat: GroupedRow[] = perCompany ? perCompany.flatMap((g) => g.accounts) : byAccount(rows, groupBy)
  const displayed = distributing ? distributeTax(flat, taxDollars) : flat
  const displayGrand = displayed.reduce((sum, a) => sum + a.total, 0)
  // Re-split the (possibly distributed) flat list back into its per-company
  // slices, in the SAME order/lengths `perCompany` used to build it — so each
  // company's own accounts and subtotal reflect the toggle without having to
  // re-key anything by account id (two companies can share one).
  let displayedByCompany: CompanyGroup[] | null = null
  if (perCompany) {
    let idx = 0
    displayedByCompany = perCompany.map((g) => {
      const accounts = displayed.slice(idx, idx + g.accounts.length)
      idx += g.accounts.length
      return { key: g.key, label: g.label, accounts }
    })
  }

  return (
    <div className={s.wrap}>
      <Divider />
      <Subtitle2>Accounts Summary</Subtitle2>
      <Table size="small" aria-label="Accounts summary" className={s.table}>
        <TableHeader>
          <TableRow>
            {showCompany && <TableHeaderCell>Company</TableHeaderCell>}
            <TableHeaderCell>{groupBy === 'expense_type' ? 'Expense type' : 'Account'}</TableHeaderCell>
            <TableHeaderCell style={{ width: 120 }} className={s.numHead}>Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedByCompany
            ? displayedByCompany.map((g) => <CompanyGroupRows key={g.key} label={g.label} accounts={g.accounts} />)
            : displayed.map((a) => (
                <TableRow key={a.key}>
                  <TableCell className={a.label === NO_ACCOUNT || a.label === NO_EXPENSE_TYPE ? s.none : undefined}>{a.label}</TableCell>
                  <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
                </TableRow>
              ))}
          <TableRow className={s.grandRow}>
            {showCompany && <TableCell />}
            {/* THE LABEL CELL — "Total" plus, inline on the same line, the
                "Distribute tax" toggle and the Reconciles pill, right-hugging
                the amount cell next door. See `totalCell`'s comment above for
                how this preserves the amount COLUMN's own alignment: nothing
                here touches the amount cell below. */}
            <TableCell>
              <div className={s.totalCell}>
                <span>Total</span>
                <div className={s.totalControls}>
                  {canDistributeTax && (
                    <Switch
                      size="small"
                      labelPosition="before"
                      label="Distribute tax"
                      checked={distribute}
                      disabled={taxDollars === 0}
                      onChange={(_e, d) => setDistribute(d.checked)}
                    />
                  )}
                  {reconciles && <DotPill pill={TINT_PILLS.green}>Reconciles</DotPill>}
                  <span className={ls.label}>{distributing ? 'Invoice Total:' : 'Invoice Subtotal:'}</span>
                </div>
              </div>
            </TableCell>
            {/* THE AMOUNT CELL — unchanged: same `width: 120`, same `s.num`
                (`text-align: right`), same bare `formatCurrency(...)` content
                as every account row's amount cell above it and as this exact
                cell before this change. Only the NUMBER shown moved (raw
                `grand` vs. the toggle's `displayGrand`, which sums the SAME
                `displayed` rows rendered above) — the cell's shape, and so its
                right-edge alignment with the column above it, did not. */}
            <TableCell className={s.num}>{formatCurrency(displayGrand)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {awaiting.length > 0 && (
        <div className={s.pillLine}>
          <DotPill pill={TINT_PILLS.yellow}>
            {formatCurrency(awaitingTotal)} awaiting a cost center
            {awaiting.length === 1 ? ' (1 line)' : ` (${awaiting.length} lines)`}
          </DotPill>
        </div>
      )}
    </div>
  )
}

/** One company's slice of the flat account pool — `accounts` is whatever the
    caller currently wants rendered for it (raw or tax-distributed; this type
    carries either). */
interface CompanyGroup {
  key: string
  label: string
  accounts: GroupedRow[]
}

/** Company groups ordered by RAW subtotal (desc) — fixed regardless of the
    "Distribute tax" toggle, so toggling it never reorders the companies —
    each holding its own accounts, descending by RAW total within it (also
    fixed: `byAccount` sorts before any distribution runs). This is the
    STRUCTURE only; `AccountsSummary` decides what `accounts` actually shows by
    re-slicing the (possibly distributed) flat pool built from THIS function's
    own `accounts` arrays, in the same order, back over them. */
function buildCompanyGroups(rows: AccountsSummaryRow[], groupBy: 'account' | 'expense_type'): CompanyGroup[] {
  const map = new Map<string, { label: string; rows: AccountsSummaryRow[]; total: number }>()
  for (const r of rows) {
    const key = r.companyId?.trim() || ''
    const label = r.companyName?.trim() || '(No company)'
    const g = map.get(key)
    if (g) { g.rows.push(r); g.total += r.total }
    else map.set(key, { label, rows: [r], total: r.total })
  }
  return [...map.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .map(([key, g]) => ({ key, label: g.label, accounts: byAccount(g.rows, groupBy) }))
}

/** One company's rows: its account lines, then its own subtotal row. A
    distinct component (not a shared flat array) so each company's rows only
    ever need a key unique among THEIR OWN siblings — the composite
    `${companyKey}-${accountKey}` keys the single flat-array version needed are
    gone because there is no longer a single flat array. */
function CompanyGroupRows({ label, accounts }: { label: string; accounts: GroupedRow[] }) {
  const s = useStyles()
  const total = accounts.reduce((sum, a) => sum + a.total, 0)
  return (
    <>
      {accounts.map((a, i) => (
        <TableRow key={a.key}>
          <TableCell>{i === 0 ? label : ''}</TableCell>
          <TableCell className={a.label === NO_ACCOUNT || a.label === NO_EXPENSE_TYPE ? s.none : undefined}>{a.label}</TableCell>
          <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
        </TableRow>
      ))}
      <TableRow className={s.subtotalRow}>
        <TableCell />
        <TableCell className={s.subtotalLabel}>{label} subtotal</TableCell>
        <TableCell className={s.num}>{formatCurrency(total)}</TableCell>
      </TableRow>
    </>
  )
}
